import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, InfoCircle } from '../../../../assets/icons/index';
import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { Stakeholder, useFormat, useLocale } from '../../../../hooks/index';
import { EventFormType, EventUnionWithPermissions } from '../../../../types/events.types';
import { toNumber } from '../../../../utils/to-number.util';
import {
  FormDropDown,
  ShareClassCombobox,
  StakeholderCombobox,
  SubEventFileUpload,
} from '../components/index';
import { FormSchema } from '../event-form.validation';
import { resolveTransactionItemDocusignFileLinks } from '../utils/resolve-linked-signature-file-links.util';

export type SecondariesStepOneProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  errors: FieldErrors<FormSchema>;
  event: EventUnionWithPermissions | null;
  formData: UseFormGetValues<FormSchema>;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const SecondariesStepOne: FC<SecondariesStepOneProps> = ({
  control,
  setFormData,
  companyId,
  formData,
  setError,
  clearErrors,
  errors,
  event,
}) => {
  const { messagesLocale } = useLocale();
  const { initialStep } = useWatch<FormSchema>({ control });
  const defaultDate = useMemo(() => new Date(+0), []);

  const {
    fields: secondariesItemsFields,
    append: secondariesItemsAppend,
    remove: secondariesItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.eventDetails.secondaries.secondariesItems',
  });

  const handleRemoveSecondariesItems = useCallback(
    (index: number) => {
      secondariesItemsRemove(index);
    },
    [secondariesItemsRemove],
  );

  const handleAppendSecondariesItems = useCallback(() => {
    secondariesItemsAppend({
      date: defaultDate,
      balance: 0,
      stakeholderFrom: { id: '', fullName: '' },
      stakeholderTo: { id: '', fullName: '' },
      shareClass: { id: '', name: '' },
      shares: 0,
      shareValue: 0,
    });
  }, [defaultDate, secondariesItemsAppend]);

  useEffect(() => {
    if (initialStep?.type === EventFormType.SECONDARIES && secondariesItemsFields.length === 0) {
      handleAppendSecondariesItems();
    }
  }, [handleAppendSecondariesItems, initialStep?.type, secondariesItemsFields.length]);

  return (
    <>
      {secondariesItemsFields.map((field, index) => (
        <SecondariesItem
          clearErrors={clearErrors}
          companyId={companyId}
          control={control}
          errors={errors}
          event={event}
          formData={formData}
          handleRemoveSecondariesItems={handleRemoveSecondariesItems}
          index={index}
          key={field.id}
          setError={setError}
          setFormData={setFormData}
          showRemoveButton={secondariesItemsFields.length > 1}
        />
      ))}
      <Button
        className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
        disabled={secondariesItemsFields.length >= 100}
        onClick={handleAppendSecondariesItems}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_ANOTHER_SHARE_TRANSFER" />
      </Button>
      <DropDown
        answer={messagesLocale['SECONDARIES_EVENT_EXPLANATION']}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_SECONDARY" />}
      />
    </>
  );
};

const SecondariesItem: FC<
  {
    handleRemoveSecondariesItems: (index: number) => void;
    index: number;
    showRemoveButton: boolean;
  } & SecondariesStepOneProps
> = ({
  control,
  setFormData,
  companyId,
  index,
  handleRemoveSecondariesItems,
  showRemoveButton,
  clearErrors,
  errors: { stepOne: errors },
  formData,
  setError,
  event,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();

  const defaultDate = new Date(+0);
  const transactionId = stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.id;
  const linkedDocusignFileLinks = resolveTransactionItemDocusignFileLinks(event, transactionId);

  const stakeholderId =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.id || '';
  const shareClassId =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.shareClass?.id || '';
  const initialShares =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.initialShares || undefined;
  const initialStakeholderFromId =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.initialStakeholderFromId;
  const initialStakeholderToId =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.initialStakeholderToId;
  const initialShareClassId =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.initialShareClassId;
  const shouldAddInitialShares =
    initialShares &&
    initialStakeholderFromId === stakeholderId &&
    initialShareClassId === shareClassId;
  const shouldSubtractInitialShares =
    initialShares &&
    initialStakeholderToId === stakeholderId &&
    initialShareClassId === shareClassId;

  const { balance } = Stakeholder.useBalance({ companyId, stakeholderId, shareClassId });

  useEffect(() => {
    if (balance) {
      setFormData(
        `stepOne.eventDetails.secondaries.secondariesItems.${index}.balance`,
        shouldAddInitialShares
          ? Number(balance) + initialShares
          : shouldSubtractInitialShares
            ? Number(balance) - initialShares
            : Number(balance),
      );
    }
  }, [
    balance,
    index,
    initialShares,
    setFormData,
    shouldAddInitialShares,
    shouldSubtractInitialShares,
  ]);

  const getFullTitle = useCallback(
    (index: number) => {
      const stakeholderFrom =
        stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.fullName;
      const stakeholderTo =
        stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderTo?.fullName;

      if (stakeholderFrom && stakeholderTo) {
        return `Share Transfer - ${stakeholderFrom} to ${stakeholderTo}`;
      }

      return 'Share Transfer';
    },
    [stepOne?.eventDetails?.secondaries?.secondariesItems],
  );

  const getTransferAmount = useCallback(
    (index: number) => {
      const shares = stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.shares;
      const shareValue = stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.shareValue;

      if (shares && shareValue) {
        return Number(shares * shareValue);
      }

      return 0;
    },
    [stepOne?.eventDetails?.secondaries?.secondariesItems],
  );

  const calculateBalance = useCallback(
    (index: number) => {
      if (balance) {
        const currentItem = stepOne?.eventDetails?.secondaries?.secondariesItems?.[index];

        if (!currentItem) {
          return Number(balance);
        }

        const filteredItems =
          stepOne?.eventDetails?.secondaries?.secondariesItems &&
          stepOne?.eventDetails?.secondaries?.secondariesItems.filter((_, i) => i !== index);

        if (!filteredItems || filteredItems.length === 0) {
          return Number(balance);
        }

        const matchingItems = filteredItems.filter(
          (item) =>
            item.stakeholderFrom?.id === currentItem.stakeholderFrom?.id &&
            item.shareClass?.id === currentItem.shareClass?.id,
        );

        if (matchingItems.length === 0) {
          return Number(balance);
        }

        const totalShares = matchingItems.reduce((sum, item) => sum + toNumber(item.shares), 0);

        return Number(balance - totalShares);
      }
      return 0;
    },
    [balance, stepOne?.eventDetails?.secondaries?.secondariesItems],
  );

  const calculateAvailableShares = useCallback(
    (index: number) => {
      const shares = stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.shares || 0;
      const balance = shouldAddInitialShares
        ? calculateBalance(index) + (initialShares || 0)
        : shouldSubtractInitialShares
          ? calculateBalance(index) - (initialShares || 0)
          : calculateBalance(index);

      return balance - shares;
    },
    [
      calculateBalance,
      initialShares,
      shouldAddInitialShares,
      shouldSubtractInitialShares,
      stepOne?.eventDetails?.secondaries?.secondariesItems,
    ],
  );

  const isStakeholdersDuplicate =
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.id &&
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderTo?.id &&
    stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.id ===
      stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderTo?.id;

  return (
    <FormDropDown
      handleRemove={() => handleRemoveSecondariesItems(index)}
      showRemoveButton={showRemoveButton}
      title={getFullTitle(index)}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              {' '}
              <AppFormattedMessage id="EVENT_DATE" />
            </span>
            <DatePicker
              onSelect={(date) =>
                setFormData(`stepOne.eventDetails.secondaries.secondariesItems.${index}.date`, date)
              }
              value={stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.date}
              wrapperClassName="w-[224px]"
            >
              <FormInput
                autoComplete="off"
                control={control}
                disabledInput
                icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
                name={`stepOne.eventDetails.secondaries.secondariesItems.${index}.date`}
                placeholder={<AppFormattedMessage id="DATE" />}
                transform={(value) => {
                  return value && new Date(value).getTime() !== defaultDate.getTime()
                    ? format(value, 'dd/MM/yyyy')
                    : '';
                }}
                wrapperClassName="w-full"
              />
            </DatePicker>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="FROM_STAKEHOLDER" />
            </span>
            <StakeholderCombobox
              companyId={companyId}
              control={control}
              name={`stepOne.eventDetails.secondaries.secondariesItems.${index}.stakeholderFrom`}
              onlyWithShares
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="TO_STAKEHOLDER" />
            </span>
            <StakeholderCombobox
              companyId={companyId}
              control={control}
              name={`stepOne.eventDetails.secondaries.secondariesItems.${index}.stakeholderTo`}
              stakeholderFromId={
                stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.id
              }
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARE_CLASS" />
            </span>
            <ShareClassCombobox
              companyId={companyId}
              control={control}
              fieldName={`stepOne.eventDetails.secondaries.secondariesItems.${index}.shareClass`}
              stakeholderFromId={
                stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.stakeholderFrom?.id
              }
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="NO_OF_SHARES" />
            </span>
            <div className="flex flex-col items-start gap-2">
              <FormInput
                control={control}
                name={`stepOne.eventDetails.secondaries.secondariesItems.${index}.shares`}
                numberOnly
                placeholder={<AppFormattedMessage id="VALUE" />}
                shouldFormatNumber
                wrapperClassName="w-[224px]"
              />
              {balance && (
                <span className="text-xs text-gray-600">
                  <AppFormattedMessage id="AVAILABLE" />{' '}
                  <AnimatedNumber integer value={Math.max(calculateAvailableShares(index), 0)} />
                </span>
              )}
            </div>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARE_VALUE" />
            </span>
            <FormInput
              control={control}
              currencySign
              maxDecimalPlaces={4}
              name={`stepOne.eventDetails.secondaries.secondariesItems.${index}.shareValue`}
              numberOnly
              placeholder={<AppFormattedMessage id="VALUE" />}
              shouldFormatNumber
              wrapperClassName="w-[224px]"
            />
          </div>
          {isStakeholdersDuplicate && (
            <div className="flex items-center gap-1">
              <InfoCircle />
              <span className="rounded text-xs font-[450] text-fireside-600">
                <AppFormattedMessage id="DIFFERENT_STAKEHOLDERS" />
              </span>
            </div>
          )}
          <div className="h-[1px] w-full bg-gray-100" />
          <div className="flex w-full items-center justify-between py-3">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="TRANSFER_AMOUNT" />
            </span>
            <div>
              <span
                className={twMerge(
                  'rounded px-3 py-1 text-sm font-[550]',
                  getTransferAmount(index) === 0
                    ? 'bg-gray-100 text-gray-300'
                    : 'bg-brand-25 text-brand-700',
                )}
              >
                <AnimatedNumber emptyDisplay="00" integer value={getTransferAmount(index)} />
              </span>
            </div>
          </div>
        </div>
        <SubEventFileUpload
          clearErrors={clearErrors}
          companyId={companyId}
          errors={errors?.eventDetails?.secondaries?.secondariesItems?.[index]?.files || undefined}
          field={`stepOne.eventDetails.secondaries.secondariesItems.${index}.files`}
          files={stepOne?.eventDetails?.secondaries?.secondariesItems?.[index]?.files || []}
          formData={formData}
          linkedDocusignFileLinks={linkedDocusignFileLinks}
          setError={setError}
          setFormData={setFormData}
          text="secondary"
        />
      </div>
    </FormDropDown>
  );
};
